/**
 * Watches a DOM element for changes and executes the specified callback
 *
 * Uses the MutationObserver Web API
 * https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver#browser_compatibility
 *
 * And (what I assume) falls back to
 * deprecated Mutation Events for unsupported browsers.
 * https://developer.mozilla.org/en-US/docs/Web/API/MutationEvent
 */
class DOMMutationObserver {
  /**
   * @param callback The callback function to run on mutation
   */
  constructor(callback = undefined) {
    this.callback = callback;

    this.hasMutationObserver = window.MutationObserver || window.WebKitMutationObserver;

    if (this.hasMutationObserver) {
      this.observer = new MutationObserver(this.callback);
    }
  }

  /**
   * START observing target node for changes
   * Please refer to MutationObserver documentation
   * https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver/observe#parameters
   *
   * @param targetNode
   * @param options
   * @returns {boolean}
   */
  observe(targetNode, options) {
    if (this.setTargetNode(targetNode) === false) {
      return false;
    }

    if (this.hasMutationObserver) {
      this.options = options;
      this.observer.observe(this.targetNode, this.options);
      return true;
    }

    if (window.addEventListener) {
      this.targetNode.addEventListener('DOMNodeInserted', this.callback, false);
      this.targetNode.addEventListener('DOMNodeRemoved', this.callback, false);
      return true;
    }
  }

  /**
   * Setter for target node
   *
   * @param targetNode DOM Element
   * @returns {boolean}
   */
  setTargetNode(targetNode) {
    if (targetNode && targetNode.nodeType === Node.ELEMENT_NODE) {
      this.targetNode = targetNode;

      return true;
    }

    console.error('Invalid target node.');
    return false;
  }

  /**
   * STOP observing target node for changes
   *
   * @returns {boolean}
   */
  disconnect() {
    if (this.hasMutationObserver) {
      this.observer.disconnect();
      return true;
    }

    if (window.addEventListener) {
      this.targetNode.removeEventListener('DOMNodeInserted', this.callback, false);
      this.targetNode.removeEventListener('DOMNodeRemoved', this.callback, false);
      return true;
    }
  }
}

export default DOMMutationObserver;
